import {clearNativeNotificationBadges} from "../utils/nativeBadge";
import { useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { DWORKS_API_BASE } from "../constants/dworksConfig";

const DEVICE_ID_KEY = "dworks_native_push_device_id";
const TOKEN_KEY = "dworks_native_push_token";
const ENABLED_KEY = "dworks_native_push_enabled";
const DEACTIVATE_MAX_ATTEMPTS = 2;
const DEACTIVATE_RETRY_DELAY_MS = 250;

function getDeviceId() {
  let value = localStorage.getItem(DEVICE_ID_KEY);
  if (!value) {
    value = globalThis.crypto?.randomUUID?.() || `android-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem(DEVICE_ID_KEY, value);
  }
  return value;
}

export function isNativePushEnabled() {
  return localStorage.getItem(ENABLED_KEY) !== "false";
}

export async function activateNativePush() {
  if (!Capacitor.isNativePlatform()) return false;
  localStorage.setItem(ENABLED_KEY, "true");
  let permission = await PushNotifications.checkPermissions();
  if (permission.receive === "prompt" || permission.receive === "prompt-with-rationale") {
    permission = await PushNotifications.requestPermissions();
  }
  if (permission.receive !== "granted") {
    localStorage.setItem(ENABLED_KEY, "false");
    throw new Error("앱 알림 권한이 허용되지 않았습니다. 휴대폰 설정에서 D-Works 알림을 허용해 주세요.");
  }
  window.dispatchEvent(new CustomEvent("dworks:native-push-status", {
    detail: { status: "checking", token: "" },
  }));
  await PushNotifications.register();
  return true;
}

async function saveToken(user, token, permissionStatus = "granted") {
  const response = await fetch(`${DWORKS_API_BASE}/api/native-push/tokens`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${user.token}`,
    },
    body: JSON.stringify({
      platform: Capacitor.getPlatform(),
      provider: Capacitor.getPlatform() === "ios" ? "apns" : "fcm",
      token,
      device_id: getDeviceId(),
      app_version: process.env.REACT_APP_VERSION || "1.0.0",
      permission_status: permissionStatus,
    }),
  });
  if (!response.ok) throw new Error(`token_api_${response.status}`);
  localStorage.setItem(TOKEN_KEY, token);
  window.dispatchEvent(new CustomEvent("dworks:native-push-status", {
    detail: { status: "registered", token },
  }));
}

export async function deactivateNativePush(user, { preservePreference = false } = {}) {
  if (!Capacitor.isNativePlatform() || !user?.token) return false;
  await clearNativeNotificationBadges();
  localStorage.removeItem("dworks_native_badge_user");
  const token = localStorage.getItem(TOKEN_KEY);

  let lastError = null;
  for (let attempt = 1; attempt <= DEACTIVATE_MAX_ATTEMPTS; attempt += 1) {
    try {
      if (token) {
        const response = await fetch(`${DWORKS_API_BASE}/api/native-push/tokens/deactivate`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
          body: JSON.stringify({ token, device_id: getDeviceId() }),
        });
        if (!response.ok) throw new Error(`token_deactivate_api_${response.status}`);
      }
      try { await PushNotifications.unregister(); } catch {}
      localStorage.removeItem(TOKEN_KEY);
      if (!preservePreference) {
        localStorage.setItem(ENABLED_KEY, "false");
        window.dispatchEvent(new CustomEvent("dworks:native-push-status", {
          detail: { status: "disabled", token: "" },
        }));
      }
      return true;
    } catch (error) {
      lastError = error;
      if (attempt < DEACTIVATE_MAX_ATTEMPTS) {
        await new Promise((resolve) => setTimeout(resolve, DEACTIVATE_RETRY_DELAY_MS));
      }
    }
  }

  console.warn("네이티브 푸시 토큰 해제 실패", lastError?.message || "unknown");
  throw lastError || new Error("token_deactivate_failed");
}
export default function NativePushBridge({ user }) {
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !user?.token) return undefined;
    let cancelled = false;
    const handles = [];

    const setup = async () => {
      const previousBadgeUser = localStorage.getItem("dworks_native_badge_user");
      if (previousBadgeUser && previousBadgeUser !== String(user.id)) await clearNativeNotificationBadges();
      if (cancelled) return;
      localStorage.setItem("dworks_native_badge_user", String(user.id));
      handles.push(await PushNotifications.addListener("registration", ({ value }) => {
        if (cancelled || !value || !isNativePushEnabled()) return;
        saveToken(user, value).catch((error) => {
          console.warn("네이티브 푸시 토큰 저장 실패", error?.message || "unknown");
        });
      }));
      handles.push(await PushNotifications.addListener("registrationError", (error) => {
        console.warn("네이티브 푸시 등록 실패", error?.error || "unknown");
      }));
      handles.push(await PushNotifications.addListener("pushNotificationActionPerformed", ({ notification }) => {
        const target = String(notification?.data?.url || "").trim();
        if (target.startsWith("/") && !target.startsWith("//")) window.location.assign(target);
      }));

      if (Capacitor.getPlatform() === "android") {
        await PushNotifications.createChannel({
          id: "dworks_default",
          name: "D-Works 업무 알림",
          description: "발주, 채팅, 출고 상태 알림",
          importance: 5,
          visibility: 1,
          vibration: true,
        });
      }

      if (!isNativePushEnabled()) {
        window.dispatchEvent(new CustomEvent("dworks:native-push-status", {
          detail: { status: "disabled", token: "" },
        }));
        return;
      }
      let permission = await PushNotifications.checkPermissions();
      if (permission.receive === "prompt") permission = await PushNotifications.requestPermissions();
      if (permission.receive !== "granted") return;
      await PushNotifications.register();
    };

    setup().catch((error) => {
      console.warn("네이티브 푸시 초기화 실패", error?.message || error);
    });
    return () => {
      cancelled = true;
      handles.forEach((handle) => handle?.remove?.());
    };
  }, [user?.id, user?.token]);

  return null;
}

